/**
 * Phase 4 Slice 1 — army tokens, hex pathing, travel time with season brake.
 * Pure module: no UI wire yet (Slice 4/render later). Composition/factory out of scope (4.5).
 *
 * Ground truth:
 * - hex geometry + routes: map.ts
 * - enter costs / flood / mountain pass: tileStats.effectiveMoveCost
 * - season labels: clock.ts
 */
import type { Season } from "./clock.ts";
import {
  cloneHostOrigin,
  cloneStacks,
  emptyHostOrigin,
  emptyStacks,
  hostOriginTotal,
  normalizeHostOrigin,
  normalizeStacks,
  stacksFromLegacy,
  stacksTotal,
  type HostOriginMap,
  type TroopStacks,
} from "./troops.ts";
import {
  cellKey,
  hexDistance,
  neighbors,
  parseKey,
  type HexCell,
  type HexMap,
} from "./map.ts";
import { effectiveMoveCost } from "./tileStats.ts";

/** Movement points an army spends per end-turn while marching. */
export const BASE_MARCH_MP = 3;

/**
 * Season multiplies spent MP (lower = slower progress).
 * Summer campaign pace; Winter deep freeze; Autumn harvest mud; Spring flood caution.
 * Tile blocks (∞ river/pass) still apply on top via effectiveMoveCost.
 */
export const SEASON_MARCH_PACE: Record<Season, number> = {
  Spring: 0.9,
  Summer: 1.15,
  Autumn: 0.75,
  Winter: 0.55,
};

export type ArmyStatus = "idle" | "marching" | "arrived" | "stuck";

export type ArmyToken = {
  id: string;
  /** Display name (host column, banner name, …). */
  label: string;
  /**
   * Spears / headcount on the token.
   * Always kept in sync with stacksTotal(stacks) when stacks are present.
   */
  strength: number;
  /**
   * Discrete unit composition on the column (5b3).
   * Optional for legacy tokens; missing ⇒ treat as generic MAA of `strength`.
   */
  stacks?: TroopStacks;
  /**
   * Ownership ledger for stacks currently on this column (banner key → stacks).
   * HOST_ORIGIN_SEAT = player-owned host peeled into the march.
   */
  hostOrigin?: HostOriginMap;
  /** Current hex key `q,r`. */
  atKey: string;
  status: ArmyStatus;
  /** Ordered path keys including start; empty when idle. */
  path: string[];
  /** Index into path for current cell (0 = start). */
  pathIndex: number;
  /** Final destination key while marching / null if idle. */
  destKey: string | null;
  /** Unused MP carried into next turn. */
  mpResidual: number;
  /** Turns spent marching on current orders. */
  turnsMarching: number;
  note: string;
};

export type MarchWorld = {
  armies: ArmyToken[];
};

export type PathOpts = {
  season: Season;
  /**
   * Prefer road edges from HexCell.routes (0.65× enter cost).
   * Default true — roads are the march-readable spine.
   */
  preferRoads?: boolean;
  /** Cap explored nodes (safety). */
  maxVisit?: number;
};

export type FoundPath = {
  /** Keys from origin → dest inclusive. */
  keys: string[];
  /** Sum of enter costs along edges (origin enter = 0). */
  totalCost: number;
  /** Hex steps (keys.length - 1). */
  steps: number;
  usedRoadEdges: number;
};

export type MarchTickResult = {
  army: ArmyToken;
  /** Hexes advanced this tick. */
  stepped: number;
  /** MP consumed this tick (before residual carry). */
  mpSpent: number;
  arrived: boolean;
  stuck: boolean;
  log: string[];
};

function cellOf(map: HexMap, key: string): HexCell | undefined {
  return map.cells.get(key);
}

/** Enter-cost for a neighbor cell under season + bridge/pass flags on that cell. */
export function enterCostForCell(cell: HexCell, season: Season): number {
  return effectiveMoveCost(cell.biome, season, {
    hasBridge: false, // bridges not yet on HexCell — river blocks in Spring stay honest
    mountainPass: cell.mountainPass,
  });
}

/** Effective MP available this turn after season brake. */
export function marchMpForSeason(season: Season, baseMp = BASE_MARCH_MP): number {
  const pace = SEASON_MARCH_PACE[season] ?? 1;
  return Math.max(0.15, baseMp * pace);
}

/** Road discount when both cells list each other (or one lists the other) in routes. */
export function isRoadEdge(from: HexCell, to: HexCell): boolean {
  const tk = cellKey(to.q, to.r);
  const fk = cellKey(from.q, from.r);
  return from.routes.includes(tk) || to.routes.includes(fk);
}

function edgeCost(
  from: HexCell,
  to: HexCell,
  season: Season,
  preferRoads: boolean,
): number {
  const raw = enterCostForCell(to, season);
  if (!Number.isFinite(raw)) return Number.POSITIVE_INFINITY;
  if (preferRoads && isRoadEdge(from, to)) {
    return Math.max(0.5, raw * 0.65);
  }
  return raw;
}

/**
 * A* path on axial hexes using season-aware enter costs.
 * Returns null if unreachable under current season locks (flood / snow pass / water).
 */
export function findPath(
  map: HexMap,
  fromKey: string,
  toKey: string,
  opts: PathOpts,
): FoundPath | null {
  if (fromKey === toKey) {
    return { keys: [fromKey], totalCost: 0, steps: 0, usedRoadEdges: 0 };
  }
  const start = cellOf(map, fromKey);
  const goal = cellOf(map, toKey);
  if (!start || !goal) return null;

  const preferRoads = opts.preferRoads !== false;
  const maxVisit = opts.maxVisit ?? Math.max(256, map.cells.size * 4);
  const season = opts.season;

  type Node = { key: string; g: number; f: number };
  const open: Node[] = [];
  const gScore = new Map<string, number>();
  const came = new Map<string, string>();
  const roadFlag = new Map<string, boolean>(); // edge into key used road?

  const goalPos = parseKey(toKey);
  const h = (key: string) => {
    const p = parseKey(key);
    // plains-ish heuristic (cost 1 per hex)
    return hexDistance(p.q, p.r, goalPos.q, goalPos.r);
  };

  gScore.set(fromKey, 0);
  open.push({ key: fromKey, g: 0, f: h(fromKey) });

  let visits = 0;
  while (open.length > 0 && visits < maxVisit) {
    visits++;
    // simple linear extract-min (map is hundreds of cells, not millions)
    let bestI = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i]!.f < open[bestI]!.f) bestI = i;
    }
    const cur = open.splice(bestI, 1)[0]!;
    if (cur.key === toKey) {
      // reconstruct
      const keys: string[] = [toKey];
      let k = toKey;
      let usedRoadEdges = 0;
      while (k !== fromKey) {
        if (roadFlag.get(k)) usedRoadEdges++;
        const prev = came.get(k);
        if (!prev) return null;
        keys.push(prev);
        k = prev;
      }
      keys.reverse();
      return {
        keys,
        totalCost: cur.g,
        steps: keys.length - 1,
        usedRoadEdges,
      };
    }

    const curCell = cellOf(map, cur.key);
    if (!curCell) continue;
    const gCur = gScore.get(cur.key) ?? Number.POSITIVE_INFINITY;

    for (const [nq, nr] of neighbors(curCell.q, curCell.r)) {
      const nk = cellKey(nq, nr);
      const nCell = cellOf(map, nk);
      if (!nCell) continue;
      const step = edgeCost(curCell, nCell, season, preferRoads);
      if (!Number.isFinite(step)) continue;
      const tentative = gCur + step;
      const prevG = gScore.get(nk);
      if (prevG !== undefined && tentative >= prevG) continue;
      came.set(nk, cur.key);
      roadFlag.set(nk, isRoadEdge(curCell, nCell));
      gScore.set(nk, tentative);
      const f = tentative + h(nk);
      const existing = open.findIndex((n) => n.key === nk);
      if (existing >= 0) open.splice(existing, 1);
      open.push({ key: nk, g: tentative, f });
    }
  }
  return null;
}

/** Estimate whole-march turns at a fixed season pace (no mid-path season change). */
export function estimateTravelTurns(
  totalCost: number,
  season: Season,
  baseMp = BASE_MARCH_MP,
): number {
  if (totalCost <= 0) return 0;
  const mp = marchMpForSeason(season, baseMp);
  return Math.max(1, Math.ceil(totalCost / mp - 1e-9));
}

export function createArmyToken(input: {
  id: string;
  label: string;
  strength: number;
  atKey: string;
  /** Optional discrete composition; defaults to generic MAA of strength. */
  stacks?: TroopStacks | null;
  /** Optional ownership map for stacks on this column. */
  hostOrigin?: HostOriginMap | null;
}): ArmyToken {
  const strength = Math.max(0, Math.floor(input.strength));
  let stacks = input.stacks != null ? normalizeStacks(input.stacks) : emptyStacks();
  if (stacksTotal(stacks) <= 0 && strength > 0) {
    stacks = stacksFromLegacy(strength);
  }
  // Prefer explicit stacks total when provided and non-empty.
  const st = stacksTotal(stacks);
  const syncStrength = st > 0 ? st : strength;
  if (st > 0 && st !== strength && strength > 0 && input.stacks == null) {
    // strength-only path already filled stacksFromLegacy
  } else if (st > 0) {
    // keep stacks; strength follows stacks
  } else {
    stacks = strength > 0 ? stacksFromLegacy(strength) : emptyStacks();
  }
  const finalStacks = stacksTotal(stacks) > 0 ? stacks : (syncStrength > 0 ? stacksFromLegacy(syncStrength) : emptyStacks());
  const finalStrength = stacksTotal(finalStacks);
  let hostOrigin = normalizeHostOrigin(input.hostOrigin);
  if (hostOriginTotal(hostOrigin) <= 0 && finalStrength > 0) {
    // leave empty — caller (answerLiegeCall) should credit; bare tokens stay unscoped
    hostOrigin = emptyHostOrigin();
  }
  return {
    id: input.id,
    label: input.label,
    strength: finalStrength,
    stacks: finalStacks,
    hostOrigin,
    atKey: input.atKey,
    status: "idle",
    path: [],
    pathIndex: 0,
    destKey: null,
    mpResidual: 0,
    turnsMarching: 0,
    note: "Idle at muster.",
  };
}

/** Resolve stacks on a token; legacy strength-only → MAA bag. */
export function armyStacks(army: ArmyToken): TroopStacks {
  if (army.stacks && stacksTotal(army.stacks) > 0) return cloneStacks(army.stacks);
  if (army.strength > 0) return stacksFromLegacy(army.strength);
  return emptyStacks();
}

/** Resolve hostOrigin on a token (may be empty). */
export function armyHostOrigin(army: ArmyToken): HostOriginMap {
  return cloneHostOrigin(army.hostOrigin);
}

/**
 * Keep strength === stacksTotal(stacks). Optionally replace stacks/origin.
 */
export function syncArmyComposition(
  army: ArmyToken,
  next?: { stacks?: TroopStacks | null; hostOrigin?: HostOriginMap | null; strength?: number },
): ArmyToken {
  let stacks = next?.stacks != null ? normalizeStacks(next.stacks) : armyStacks(army);
  const hostOrigin =
    next?.hostOrigin !== undefined
      ? normalizeHostOrigin(next.hostOrigin)
      : cloneHostOrigin(army.hostOrigin);
  if (next?.strength != null && next.stacks == null) {
    const target = Math.max(0, Math.floor(next.strength));
    // scale not imported here to avoid cycle — caller should pass stacks
    if (stacksTotal(stacks) <= 0) stacks = stacksFromLegacy(target);
    // If only strength provided, rebuild as MAA when empty else keep and trust caller
  }
  const strength = stacksTotal(stacks);
  return {
    ...army,
    stacks,
    hostOrigin,
    strength,
  };
}

export function createMarchWorld(armies: ArmyToken[] = []): MarchWorld {
  return { armies: armies.map((a) => ({ ...a, path: [...a.path] })) };
}

/**
 * Assign a path and put the token in marching status.
 * Fails soft if path null / empty destination.
 */
export function beginMarch(
  army: ArmyToken,
  path: FoundPath | null,
  destKey: string,
): ArmyToken {
  if (!path || path.keys.length === 0) {
    return {
      ...army,
      status: "stuck",
      path: [],
      pathIndex: 0,
      destKey,
      note: `No path to ${destKey} under current season locks.`,
    };
  }
  if (path.keys[0] !== army.atKey) {
    return {
      ...army,
      status: "stuck",
      note: `Path does not start at army (${army.atKey}).`,
    };
  }
  if (path.steps === 0 || path.keys[path.keys.length - 1] === army.atKey) {
    return {
      ...army,
      status: "arrived",
      path: path.keys,
      pathIndex: 0,
      destKey: army.atKey,
      note: `${army.label} already at objective.`,
    };
  }
  return {
    ...army,
    status: "marching",
    path: path.keys,
    pathIndex: 0,
    destKey,
    mpResidual: 0,
    turnsMarching: 0,
    note: `${army.label} marches on ${destKey} (${path.steps} hexes, cost ${path.totalCost.toFixed(1)}).`,
  };
}

/**
 * One end-turn of movement along a frozen path.
 * If the next hex becomes impassable (season change), status → stuck and path clears progress note.
 */
export function tickArmyMarch(
  map: HexMap,
  army: ArmyToken,
  season: Season,
  baseMp = BASE_MARCH_MP,
): MarchTickResult {
  const log: string[] = [];
  if (army.status === "arrived") {
    return { army, stepped: 0, mpSpent: 0, arrived: true, stuck: false, log };
  }
  if (army.status === "idle") {
    return { army, stepped: 0, mpSpent: 0, arrived: false, stuck: false, log };
  }
  if (army.status === "stuck" || army.path.length < 2) {
    const stuckA = {
      ...army,
      status: "stuck" as const,
      note: army.note || "March broken — no path.",
    };
    return { army: stuckA, stepped: 0, mpSpent: 0, arrived: false, stuck: true, log: [stuckA.note] };
  }

  let mp = army.mpResidual + marchMpForSeason(season, baseMp);
  const mpBudget = mp;
  let idx = army.pathIndex;
  let atKey = army.atKey;
  let stepped = 0;
  const path = army.path;

  while (idx < path.length - 1 && mp > 1e-9) {
    const nextKey = path[idx + 1]!;
    const from = cellOf(map, atKey);
    const to = cellOf(map, nextKey);
    if (!from || !to) {
      const stuckA: ArmyToken = {
        ...army,
        atKey,
        pathIndex: idx,
        status: "stuck",
        mpResidual: 0,
        turnsMarching: army.turnsMarching + 1,
        note: `${army.label} lost the road (missing hex ${nextKey}).`,
      };
      log.push(stuckA.note);
      return { army: stuckA, stepped, mpSpent: mpBudget - mp, arrived: false, stuck: true, log };
    }
    const cost = edgeCost(from, to, season, true);
    if (!Number.isFinite(cost)) {
      const stuckA: ArmyToken = {
        ...army,
        atKey,
        pathIndex: idx,
        path,
        status: "stuck",
        mpResidual: 0,
        turnsMarching: army.turnsMarching + 1,
        note: `${army.label} halted short of ${nextKey} — ${season} closed the way.`,
      };
      log.push(stuckA.note);
      return {
        army: stuckA,
        stepped,
        mpSpent: mpBudget - mp,
        arrived: false,
        stuck: true,
        log,
      };
    }
    if (cost > mp + 1e-9) {
      // not enough MP for next hex — bank residual
      break;
    }
    mp -= cost;
    idx += 1;
    atKey = nextKey;
    stepped += 1;
  }

  const arrived = idx >= path.length - 1 && atKey === path[path.length - 1];
  const next: ArmyToken = {
    ...army,
    atKey,
    pathIndex: idx,
    path,
    mpResidual: arrived ? 0 : mp,
    turnsMarching: army.turnsMarching + 1,
    status: arrived ? "arrived" : "marching",
    note: arrived
      ? `${army.label} arrives at ${atKey} after ${army.turnsMarching + 1} march turn(s).`
      : `${army.label} advances ${stepped} hex → ${atKey} (${season} pace).`,
  };
  log.push(next.note);
  return {
    army: next,
    stepped,
    mpSpent: mpBudget - (arrived ? 0 : mp),
    arrived,
    stuck: false,
    log,
  };
}

/** Tick every marching army; leave idle/arrived untouched. */
export function tickMarchWorld(
  map: HexMap,
  world: MarchWorld,
  season: Season,
  baseMp = BASE_MARCH_MP,
): { world: MarchWorld; log: string[] } {
  const log: string[] = [];
  const armies = world.armies.map((a) => {
    if (a.status !== "marching") return a;
    const r = tickArmyMarch(map, a, season, baseMp);
    log.push(...r.log);
    return r.army;
  });
  return { world: { armies }, log };
}

/**
 * Peel strength from seat arms.host into a map token at seat (does not mutate call — caller applies).
 */
export function musterHostToken(input: {
  id?: string;
  hostStrength: number;
  seatKey: string;
  label?: string;
  stacks?: TroopStacks | null;
  hostOrigin?: HostOriginMap | null;
}): { token: ArmyToken; hostLeft: number } {
  const strength = Math.max(0, Math.floor(input.hostStrength));
  const token = createArmyToken({
    id: input.id ?? `host-${input.seatKey}`,
    label: input.label ?? "Host column",
    strength,
    atKey: input.seatKey,
    stacks: input.stacks,
    hostOrigin: input.hostOrigin,
  });
  return { token, hostLeft: 0 };
}


export function upsertArmy(world: MarchWorld, army: ArmyToken): MarchWorld {
  const i = world.armies.findIndex((a) => a.id === army.id);
  if (i < 0) return { armies: [...world.armies, army] };
  const armies = [...world.armies];
  armies[i] = army;
  return { armies };
}

/** Readable one-liner for HUD / inbox later. */
export function describeArmy(army: ArmyToken): string {
  const head = `${army.label} (${army.strength}) @ ${army.atKey}`;
  if (army.status === "marching" && army.destKey) {
    const left = Math.max(0, army.path.length - 1 - army.pathIndex);
    return `${head} → ${army.destKey} · ${left} hex left · ${army.turnsMarching}t on road`;
  }
  if (army.status === "arrived") return `${head} · arrived`;
  if (army.status === "stuck") return `${head} · stuck — ${army.note}`;
  return `${head} · idle`;
}
